
import React, { useState } from 'react';
import { ArrowLeft, Reply, ReplyAll, Forward, Trash2, MoreVertical, ChevronDown, MailOpen, Printer, FolderInput, Inbox, FileEdit, Paperclip, Loader2 } from 'lucide-react';
import { downloadEmailAttachment } from '@/services/api/email';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Email } from '@/context/email/types';
import { useEmail } from '@/context/EmailContext';
import { formatDistanceToNow } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { sanitizeEmailHtml } from '@/utils/emailHtml';

interface EmailDetailContentProps {
  email: Email;
  showCompose: boolean;
  composeType: 'reply' | 'reply-all' | 'forward' | null;
  onShowCompose: (show: boolean) => void;
  onSetComposeType: (type: 'reply' | 'reply-all' | 'forward' | null) => void;
  onBack: () => void;
}

const folderLabel = (folder: string) =>
  folder ? folder.charAt(0).toUpperCase() + folder.slice(1) : 'Inbox';

const formatSize = (bytes: number) => {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const EmailDetailContent = ({
  email,
  showCompose,
  composeType,
  onShowCompose,
  onSetComposeType,
  onBack
}: EmailDetailContentProps) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { moveToTrash, moveToFolder, markAsUnread } = useEmail();
  const { toast } = useToast();

  const handleReply = () => {
    onSetComposeType('reply');
    onShowCompose(true);
  };

  const handleReplyAll = () => {
    onSetComposeType('reply-all');
    onShowCompose(true);
  };

  const handleForward = () => {
    onSetComposeType('forward');
    onShowCompose(true);
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await moveToTrash(email.id);
      setShowDeleteDialog(false);
      toast({
        title: "Email moved to trash",
        description: "The email has been moved to trash successfully.",
      });
      onBack();
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to move email to trash: ${error?.message || 'Unknown error'}`,
        variant: "destructive",
      });
    }
  };

  const handleMarkUnread = async () => {
    try {
      await markAsUnread(email.id);
      onBack();
    } catch {
      toast({ title: "Error", description: "Failed to mark as unread", variant: "destructive" });
    }
  };

  const handleMoveToFolder = async (targetFolder: string) => {
    try {
      await moveToFolder(email.id, targetFolder);
      toast({ title: "Email moved", description: `Moved to ${folderLabel(targetFolder)}.` });
      onBack();
    } catch {
      toast({ title: "Error", description: "Failed to move email", variant: "destructive" });
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (!printWindow) return;
    const safeBody = email.body_html
      ? sanitizeEmailHtml(email.body_html)
      : `<pre style="white-space:pre-wrap;font-family:inherit;">${email.body || ''}</pre>`;
    printWindow.document.write(`<!doctype html>
      <html><head><title>${email.subject || 'Email'}</title>
      <style>body{font-family:system-ui,sans-serif;padding:24px;color:#111;} img{max-width:100%;height:auto;}</style>
      </head><body>
      <h2 style="margin:0 0 4px;">${email.subject || '(No subject)'}</h2>
      <p style="color:#555;font-size:13px;margin:0 0 16px;">
        From: ${email.from_name || ''} &lt;${email.from}&gt; &middot; To: ${(Array.isArray(email.to) ? email.to.join(', ') : email.to) || ''} &middot; ${new Date(email.date).toLocaleString()}
      </p>
      <hr style="border:none;border-top:1px solid #ddd;margin:0 0 16px;" />
      ${safeBody}
      </body></html>`);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const attachments = email.attachments || [];
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // Downloads go through the auth-gated backend endpoint (works for both
  // IMAP and Hostinger messages) — a plain <a href> can't carry the JWT.
  const handleDownloadAttachment = async (attachment: any, index: number) => {
    const attachmentId = attachment.id;
    if (!attachmentId) return;
    const key = String(attachmentId);
    setDownloadingId(key);
    try {
      await downloadEmailAttachment(email.id, attachmentId, attachment.name || `Attachment ${index + 1}`);
    } catch (error: any) {
      toast({
        title: 'Download failed',
        description: error?.message || 'Could not download the attachment.',
        variant: 'destructive',
      });
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Email Header with Actions */}
      <div className="border-b p-4 bg-card">
        <div className="flex items-center justify-between mb-4">
          <Button variant="ghost" size="sm" onClick={onBack} className="text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {folderLabel(email.folder)}
          </Button>

          <div className="flex items-center gap-2">
            {/* Split button: Reply All is the default action, Reply lives in the dropdown */}
            <div className="flex items-center">
              <Button variant="outline" size="sm" onClick={handleReplyAll} className="rounded-r-none border-r-0">
                <ReplyAll className="h-4 w-4 mr-2" />
                Reply All
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="rounded-l-none px-2">
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleReply}>
                    <Reply className="h-4 w-4 mr-2" />
                    Reply
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <Button variant="outline" size="sm" onClick={handleForward}>
              <Forward className="h-4 w-4 mr-2" />
              Forward
            </Button>
            <Button variant="outline" size="sm" onClick={handleDelete}>
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleMarkUnread}>
                  <MailOpen className="h-4 w-4 mr-2" />
                  Mark as unread
                </DropdownMenuItem>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <FolderInput className="h-4 w-4 mr-2" />
                    Move to folder
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    {email.folder !== 'inbox' && (
                      <DropdownMenuItem onClick={() => handleMoveToFolder('inbox')}>
                        <Inbox className="h-4 w-4 mr-2" />
                        Inbox
                      </DropdownMenuItem>
                    )}
                    {email.folder !== 'drafts' && (
                      <DropdownMenuItem onClick={() => handleMoveToFolder('drafts')}>
                        <FileEdit className="h-4 w-4 mr-2" />
                        Drafts
                      </DropdownMenuItem>
                    )}
                    {email.folder !== 'trash' && (
                      <DropdownMenuItem onClick={() => handleMoveToFolder('trash')}>
                        <Trash2 className="h-4 w-4 mr-2" />
                        Trash
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuItem onClick={handlePrint}>
                  <Printer className="h-4 w-4 mr-2" />
                  Print
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Email Metadata */}
        <div className="space-y-3">
          <h1 className="text-xl font-semibold">{email.subject}</h1>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-sm font-medium">
                    {email.from_name ? email.from_name.charAt(0).toUpperCase() : email.from.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <div className="font-medium">{email.from_name || email.from}</div>
                  <div className="text-sm text-muted-foreground">{email.from}</div>
                </div>
              </div>
            </div>

            <div className="text-sm text-muted-foreground">
              {formatDistanceToNow(new Date(email.date), { addSuffix: true })}
            </div>
          </div>

          {/* Recipients */}
          <div className="space-y-1 text-sm">
            <div>
              <span className="text-muted-foreground">To: </span>
              <span>{Array.isArray(email.to) ? email.to.join(', ') : email.to}</span>
            </div>
            {email.cc && email.cc.length > 0 && (
              <div>
                <span className="text-muted-foreground">CC: </span>
                <span>{Array.isArray(email.cc) ? email.cc.join(', ') : email.cc}</span>
              </div>
            )}
          </div>

          {/* Status Badges */}
          <div className="flex items-center gap-2">
            {!email.read && <Badge variant="secondary">Unread</Badge>}
            {attachments.length > 0 && (
              <Badge variant="outline">{attachments.length} attachment(s)</Badge>
            )}
          </div>
        </div>
      </div>

      {/* Email Body */}
      <div className="flex-1 p-6 overflow-auto">
        <div className="prose prose-sm max-w-none dark:prose-invert">
          {email.body_html ? (
            <div dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(email.body_html) }} />
          ) : email.body ? (
            <div className="whitespace-pre-wrap">{email.body}</div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-sm text-muted-foreground italic">
                This message has no body content.
              </p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                The mail server returned an empty body for this message.
              </p>
            </div>
          )}
        </div>

        {/* Attachments — download only when the backend provides a URL */}
        {attachments.length > 0 && (
          <div className="mt-6 border-t pt-4">
            <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
              <Paperclip className="h-4 w-4 text-muted-foreground" />
              Attachments
            </h4>
            <div className="flex flex-wrap gap-2">
              {attachments.map((attachment: any, index: number) => {
                const key = String(attachment.id || index);
                const isDownloading = downloadingId === key;
                const chip = (
                  <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm">
                    {isDownloading ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground flex-shrink-0" />
                    ) : (
                      <Paperclip className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                    )}
                    <span className="truncate max-w-[200px]">{attachment.name || `Attachment ${index + 1}`}</span>
                    {attachment.size > 0 && (
                      <span className="text-xs text-muted-foreground">{formatSize(attachment.size)}</span>
                    )}
                  </div>
                );
                if (attachment.id) {
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => handleDownloadAttachment(attachment, index)}
                      disabled={isDownloading}
                      className="hover:opacity-80 disabled:opacity-60"
                      title="Download attachment"
                    >
                      {chip}
                    </button>
                  );
                }
                return attachment.url ? (
                  <a key={key} href={attachment.url} download={attachment.name} className="hover:opacity-80">
                    {chip}
                  </a>
                ) : (
                  <div key={key}>{chip}</div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Move email to trash?</AlertDialogTitle>
            <AlertDialogDescription>
              This email will be moved to the trash folder. You can restore it later from the trash.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete}>
              Move to Trash
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
